/* eslint-disable max-lines */
import { ShareResource } from '@hakimo-ui/hakimo/feature-shared';
import {
  AIRecommendation,
  LocationAlarm,
  StatusType,
} from '@hakimo-ui/hakimo/types';
import {
  isEscalationInScope,
  selectActiveIncident,
  toast,
} from '@hakimo-ui/hakimo/util';
import { Label } from '@hakimo-ui/shared/ui-base';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useMemo, useState } from 'react';
import {
  mapStatusToStatusType,
  mapStatusTypeToLabelType,
} from '../../../shared/util/common';
import AlarmMenu from '../alarm-menu/AlarmMenu';
import AlarmStatus from '../alarm-status/AlarmStatus';
import LocationAlarmIncident from '../location-alarm-incident/LocationAlarmIncident';
import { ViewMode } from '../types';
dayjs.extend(utc);

interface Props {
  alarm: LocationAlarm;
  viewMode: ViewMode;
  onChangeViewMode: (viewMode: ViewMode) => void;
  onStatusChange?: (status: StatusType) => void;
  showResolveAction?: boolean;
  cameraNames?: string[];
  aiOperatorRep?: AIRecommendation;
}

export function Header(props: Props) {
  const {
    alarm,
    viewMode,
    onChangeViewMode,
    onStatusChange,
    showResolveAction = true,
    cameraNames,
    aiOperatorRep,
  } = props;
  const [isStatusEditing, setIsStatusEditing] = useState(false);
  const [openShareAlarm, setOpenShareAlarm] = useState(false);

  const onCopyLink = async () => {
    const timestamp = alarm.alarmTime;
    const date = dayjs.utc(timestamp);
    const partitionKey = date.format('YYYYMM');
    const locationAlarmLink = `${window.location.origin}/location-alarms/${alarm.id}?partitionKey=${partitionKey}`;

    try {
      const permissionStatus = await navigator.permissions.query({
        name: 'clipboard-write' as PermissionName,
      });

      if (permissionStatus.state === 'denied') {
        toast(
          'Clipboard permission denied, Please enable it in browser site settings',
          {
            type: 'warning',
          }
        );
        return;
      }
    } catch {
      // Permission query not supported in this browser, continue to attempt clipboard write
    }

    try {
      await navigator.clipboard.writeText(locationAlarmLink);
      toast('Location alarm link copied to clipboard');
    } catch (err) {
      toast('Failed to copy link to clipboard', { type: 'error' });
      Sentry.captureMessage('Error copying text to clipboard');
    }
  };

  const onShare = () => {
    setOpenShareAlarm(true);
  };

  // `locationIncidents` carries every incident at the location, so narrow to
  // this alarm's scope before picking one -- otherwise another source group's
  // live incident wins and Resolve closes somebody else's escalation. The one
  // picked may be anchored to a sibling alarm, which is why the resolve path
  // below keys off `incident.id` rather than this alarm's id.
  const locationIncident = useMemo(
    () =>
      selectActiveIncident(
        (alarm.locationIncidents ?? []).filter((incident) =>
          isEscalationInScope(incident, {
            alarmGroupId: alarm.id,
            sourceGroupId: alarm.sourceGroupId,
            locationAlarmTypeId: alarm.locationAlarmTypeId,
            product: alarm.product,
          })
        )
      ),
    [
      alarm.locationIncidents,
      alarm.id,
      alarm.sourceGroupId,
      alarm.locationAlarmTypeId,
      alarm.product,
    ]
  );

  return (
    <>
      {locationIncident && (
        <LocationAlarmIncident
          incident={locationIncident}
          locationAlarmId={alarm.id}
          onStatusChange={onStatusChange}
          cameraNames={cameraNames}
          aiOperatorRep={aiOperatorRep}
          location={alarm.location}
        />
      )}
      <div className="flex h-12 flex-shrink-0 items-center justify-between">
        <div className="flex items-center gap-4 px-4">
          {!isStatusEditing && (
            <span className="flex flex-shrink-0 flex-col">
              <span title={`#${alarm.id} - ${alarm.location?.name}`}>
                {alarm.location?.name}
              </span>
              <span className="dark:text-ondark-text-2 text-onlight-text-2 text-xs">
                {alarm.location.description}
              </span>
            </span>
          )}
          {!isStatusEditing && (
            <div className="h-4 border-l border-black/20 dark:border-white/20"></div>
          )}
          {showResolveAction ? (
            <AlarmStatus
              isEdit={isStatusEditing}
              onChangeEditStatus={setIsStatusEditing}
              alarm={alarm}
              onStatusChange={onStatusChange}
            />
          ) : (
            <Label
              type={mapStatusTypeToLabelType(
                mapStatusToStatusType(alarm.status)
              )}
            >
              {mapStatusToStatusType(alarm.status)}
            </Label>
          )}
        </div>

        {!isStatusEditing && (
          <AlarmMenu
            onChangeViewMode={onChangeViewMode}
            onCopyLink={onCopyLink}
            onShare={onShare}
            viewMode={viewMode}
          />
        )}

        {openShareAlarm && (
          <ShareResource
            resourceId={alarm.id}
            resourceType="location-alarm"
            onClose={() => setOpenShareAlarm(false)}
            timestamp={alarm.alarmTime}
          />
        )}
      </div>
    </>
  );
}
export default Header;
